import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import whitelabel from "../../whitelabel";
import * as styles from "./rules.module.scss";
import * as tableStyles from "../../styles/table.module.scss";
import ScreenContainer from "../../screens/screen-container";
import { makeStyleable } from "../../helper/icon-helper";
import ListHeader from "../../elements/list-header";
import {
	CreateNewRuleParams,
	VelavuAPI,
	VelavuRule,
	VelavuRuleCategory,
} from "velavu-js-api";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import RulesFilter from "./rules-filter";
import LoadingDots from "../../elements/loading-dots";
import RuleTableRow from "./rule-table-row";
import { useVelavuToast } from "../../elements/velavu-toast";
import { ToastNotificationExternalError } from "../../elements/toast-notification-builtins";
import { useNavigate } from "react-router";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuButton from "../../elements/velavu-button";
import IconStar from "../../dynamicicons/icon-star";
import { ScreenContentContainer } from "../../screens/screen-content-container";
import { WeightedTable } from "../../elements/weighted-table";
import { useHasPermission } from "../sign-in-context/sign-in-context";

export default function Rules() {
	const { t } = useTranslation();

	const canWriteRules = useHasPermission("rules:write");

	useEffect(() => {
		document.title = `${whitelabel.name} | ${t("rules.page_title")}`;
	}, [t]);

	const navigate = useNavigate();
	const { pushModal } = useVelavuModal();

	//Download rules
	const [rules, setRules] = useState<null | VelavuRule[]>(null);
	useEffect(() => {
		VelavuAPI.rules
			.getAllRules()
			.then((rules) => setRules(rules))
			.catch((error) => console.warn("Failed to fetch rules:", error));
	}, [setRules]);
	useProvideContentRefreshable(
		useCallback(async () => {
			setRules(null);
			const newRules = await VelavuAPI.rules.getAllRules();
			setRules(newRules);
		}, [setRules]),
	);

	const updateRule = useCallback(
		(updatedRule: VelavuRule) => {
			setRules((prevRules) => {
				if (prevRules === null) {
					return null;
				}

				const ruleIndex = prevRules.findIndex(
					(rule) => rule.id === updatedRule.id,
				);
				if (ruleIndex === -1) {
					return prevRules;
				}

				const updatedRules = [...prevRules];
				updatedRules[ruleIndex] = updatedRule;
				return updatedRules;
			});
		},
		[setRules],
	);

	//Filter parameters
	const [searchText, setSearchText] = useState("");
	const [filterCategory, setFilterCategory] =
		useState<null | VelavuRuleCategory>(VelavuRuleCategory.User);

	const filteredRules = useMemo(() => {
		if (rules === null) {
			return null;
		}

		let sanitizedSearchText: string | null = searchText
			.trim()
			.toLowerCase();
		if (sanitizedSearchText.length === 0) {
			sanitizedSearchText = null;
		}

		//Check against search filter
		return rules.filter((rule) => {
			if (
				sanitizedSearchText !== null &&
				(rule.name === undefined ||
					!rule.name.toLowerCase().includes(searchText))
			) {
				return false;
			}

			//Check against the category filter
			if (filterCategory !== null && rule.category !== filterCategory) {
				return false;
			}

			return true;
		});
	}, [rules, searchText, filterCategory]);

	const { pushToast } = useVelavuToast();

	//Actions
	const setRuleEnabled = useCallback(
		async (ruleID: string, enabled: boolean) => {
			//Find the rule in the state
			let currentRule: VelavuRule | null = null;
			if (rules !== null) {
				const ruleIndex = rules.findIndex((rule) => rule.id === ruleID);
				if (ruleIndex !== -1) {
					currentRule = rules[ruleIndex];

					//Update the rule immediately in the state
					const updatedRules = [...rules];
					updatedRules[ruleIndex] = {
						...currentRule,
						enabled: enabled,
					};
					setRules(updatedRules);
				}
			}

			//Update the rule on the backend
			let updatedRule: VelavuRule;
			try {
				updatedRule = await VelavuAPI.rules.modifyExistingRule(ruleID, {
					enabled: enabled,
				});
			} catch (error) {
				console.warn("Failed to update rule:", error);

				//Push a toast notification
				pushToast((close) => (
					<ToastNotificationExternalError onDismiss={close} />
				));

				//Revert the state change
				if (currentRule !== null) {
					updateRule(currentRule);
				}

				return;
			}

			//Set the updated rule in the state
			updateRule(updatedRule);
		},
		[pushToast, rules, setRules, updateRule],
	);

	const duplicateRule = useCallback(
		async (rule: VelavuRule) => {
			const newRuleParams: CreateNewRuleParams = {
				name: `${rule.name ?? ""} COPY`,
				description: rule.description,
				category: rule.category,
				enabled: rule.enabled,
				condition: rule.condition,
				action: rule.action,
			};

			let newRule: VelavuRule;
			try {
				newRule = await VelavuAPI.rules.createNewRule(newRuleParams);
			} catch (error) {
				console.warn("Failed to duplicate rule:", error);

				//Push a toast notification
				pushToast((close) => (
					<ToastNotificationExternalError onDismiss={close} />
				));

				return;
			}

			//Add the new rule to the state
			setRules((prevRules) => {
				if (prevRules === null) {
					return null;
				}
				return [newRule, ...prevRules];
			});
		},
		[pushToast],
	);

	const deleteRule = useCallback(
		async (ruleID: string) => {
			//Remove the rule from the state
			setRules((prevRules) => {
				if (prevRules === null) {
					return null;
				}
				return prevRules.filter((r) => r.id !== ruleID);
			});

			//Delete the rule on the backend
			try {
				await VelavuAPI.rules.deleteRule(ruleID);
			} catch (error) {
				console.warn("Failed to delete rule:", error);

				//Push a toast notification
				pushToast((close) => (
					<ToastNotificationExternalError onDismiss={close} />
				));

				return;
			}
		},
		[pushToast],
	);

	const promptDeleteRule = useCallback(
		async (rule: VelavuRule) => {
			const result = await pushModal<boolean>((close) => (
				<VelavuAlertModal>
					<VelavuAlertModalTitle>
						{t("rules.delete_modal_title")}
					</VelavuAlertModalTitle>

					<VelavuAlertModalContent>
						{rule.name ?? t("rules.unnamed_rule")}
					</VelavuAlertModalContent>

					<VelavuAlertModalActions>
						<VelavuButton
							label={t("common.cancel")}
							variant="outlined"
							fullWidth
							onClick={() => close(false)}
						/>

						<VelavuButton
							label={t("common.delete")}
							variant="danger"
							fullWidth
							onClick={() => close(true)}
						/>
					</VelavuAlertModalActions>
				</VelavuAlertModal>
			));

			if (result === true) {
				await deleteRule(rule.id);
			}
		},
		[deleteRule, pushModal, t],
	);

	return (
		<ScreenContainer>
			<ListHeader
				icon={makeStyleable(IconStar)}
				title={t("sidebar.nav.rules")}
				search={{
					searchPlaceholder: t("rules.search_placeholder"),
					searchText: searchText,
					setSearchText: setSearchText,
				}}
				add={
					canWriteRules
						? {
								label: t("rules.new_rule"),
								route: "/rules/create",
							}
						: undefined
				}
			/>

			<ScreenContentContainer className={styles.container}>
				<RulesFilter
					category={filterCategory}
					onChangeCategory={setFilterCategory}
				/>

				{filteredRules === null ? (
					<div className={styles.loading}>
						<LoadingDots
							size={40}
							color={whitelabel.palette.secondary500}
						/>
					</div>
				) : (
					<div className={styles.tableWrapper}>
						<WeightedTable
							className={styles.table}
							layout={[
								{ flex: 1 },
								{ width: 120 },
								{ width: 140 },
								{ width: 80, alignment: "center" },
								{ width: 80, alignment: "center" },
							]}
						>
							<thead
								className={tableStyles.scrollableTableHeader}
							>
								<tr>
									<th>{t("rules.table_name")}</th>
									<th>{t("rules.table_type")}</th>
									<th>{t("rules.table_last_updated")}</th>
									<th>{t("rules.table_enabled")}</th>
									<th>{t("rules.table_action")}</th>
								</tr>
							</thead>

							<tbody>
								{filteredRules?.map((rule) => {
									const clickable =
										rule.category ===
										VelavuRuleCategory.User;

									return (
										<RuleTableRow
											key={rule.id}
											rule={rule}
											clickable={clickable}
											onClick={
												!clickable
													? undefined
													: () => {
															navigate(
																`/rules/${rule.id}`,
																{
																	state: {
																		rule,
																	},
																},
															);
														}
											}
											onDuplicate={() =>
												duplicateRule(rule)
											}
											onDelete={() =>
												promptDeleteRule(rule)
											}
											onSetEnabled={(enabled) =>
												setRuleEnabled(rule.id, enabled)
											}
										/>
									);
								})}
							</tbody>
						</WeightedTable>
					</div>
				)}
			</ScreenContentContainer>
		</ScreenContainer>
	);
}
